import {
  closest,
  createElement,
  focusFirstChild,
  focusLastChild,
  focusNextChild,
  focusPreviousChild,
  replaceChildren,
} from "../utils";

export default class NavigationMenu extends HTMLElement {
  /**
   * Define the element in the document's custom element registry.
   *
   * @param {string} [tag] The tag to use in the element definition
   */
  static define(tag = "ifrs-navigation-menu") {
    if (!customElements.get(tag)) {
      customElements.define(tag, this);
    }
  }

  constructor() {
    super();
    this.cleanup = new Set();
  }

  /**
   * A collection for side effect cleanup functions. Each function in the set
   * is called and removed when the component is disconnected.
   *
   * @type {Set<() => void>}
   */
  cleanup;

  /**
   * Set up the component and manage event handlers when the component is
   * connected to the DOM.
   */
  connectedCallback() {
    // Exit early if the component is not connected.
    if (!this.isConnected) {
      return;
    }

    // Set up component markup
    this.setup();

    /**
     *
     * @param {KeyboardEvent} event
     */
    const handleKeyDown = (event) => {
      if (event.defaultPrevented) {
        return;
      }
      if ("ArrowUp" === event.key || "ArrowLeft" === event.key) {
        event.preventDefault();
        focusPreviousChild(closest(event.target, ".menu"));
      }
      if ("ArrowDown" === event.key || "ArrowRight" === event.key) {
        event.preventDefault();
        focusNextChild(closest(event.target, ".menu"));
      }
      if ("Home" === event.key) {
        event.preventDefault();
        focusFirstChild(closest(event.target, ".menu"));
      }
      if ("End" === event.key) {
        event.preventDefault();
        focusLastChild(closest(event.target, ".menu"));
      }
    };

    this.addEventListener("keydown", handleKeyDown);
    this.cleanup.add(() => this.removeEventListener("keydown", handleKeyDown));
  }

  /**
   * Clean up side effects when the component is disconnected.
   */
  disconnectedCallback() {
    // Call and delete all cleanup functions
    if (this.cleanup) {
      this.cleanup.forEach((fn) => fn());
      this.cleanup.clear();
    }
  }

  /**
   * Set up the component markup.
   *
   * Note that this method may be called repeatedly and should be idempotent.
   */
  setup() {
    this.querySelectorAll("ul:not(.menu)").forEach((list) => {
      list.classList.add("menu");
    });

    if (!this.hasAttribute("orientation")) {
      this.setAttribute("orientation", "horizontal");
    }

    const orientation = this.getAttribute("orientation");

    this.querySelectorAll("details").forEach((details) => {
      /** @type {HTMLElement} */
      let el;
      if (orientation === "horizontal" && !closest(details, ".menu .menu")) {
        el = createElement("ifrs-popover");
      } else {
        el = createElement("ifrs-disclosure");
      }
      details.replaceWith(el);
      replaceChildren(el, details);
    });
  }
}
